import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { ScrollContainer } from 'theme/Global.styles';
import Flex from 'styled-flex-component';
import DocumentDownload from 'components/DocumentDownload/DocumentDownload';
import { Col, Row } from '@zendeskgarden/react-grid';
import { get, map, replace, toUpper } from 'lodash';
import { XL, MD, SM } from '@zendeskgarden/react-typography';
import { metallurgyShape } from 'propTypesObjects.js';
import AlloyComposition from 'components/AlloyComposition/AlloyComposition';
import { webRefactorSwitch } from 'utility/environment';
import {
  DetailsWrapper,
  AttributeRow,
  AttributeWrapper,
  Attribute,
} from './CategoryAlloyHero.styles';
import CategoryAlloyHeroRedesign from './CategoryAlloyHeroRedesign';

function CategoryAlloyHero({
  alloyGroupDetails,
  alloyDetails,
  alloyGroupSlug,
  alloySlug,
  categories,
}) {
  const alloyGroupDetailsResult = alloyGroupDetails?.result;
  const alloyDetailsResult = alloyDetails?.result;

  const files = alloyDetailsResult?.files;
  const groupName =
    alloyDetailsResult?.metallurgy_group_name ||
    alloyGroupDetailsResult?.name_long ||
    alloyGroupDetailsResult?.name ||
    replace(alloyGroupSlug || '', /-/g, ' ') ||
    ' ';
  const alloyName = alloyDetailsResult?.name || alloySlug || '';
  const alloyDesc = alloyDetailsResult?.description;

  const renderAlloyCompositionTitle = useCallback(
    () => (
      <SM style={{ fontSize: '13px' }} slate as="h3" bold>
        Element Composition
      </SM>
    ),
    []
  );

  return (
    <div>
      {webRefactorSwitch ? (
        <CategoryAlloyHeroRedesign
          groupName={groupName}
          alloyName={alloyName}
          alloyDesc={alloyDesc}
          alloyDetails={alloyDetailsResult}
          files={files}
          categories={categories}
        />
      ) : (
        <div style={{ marginBottom: '40px' }}>
          <Row>
            <Col>
              {alloyName ? (
                <XL
                  navy
                  bold
                  as="h1"
                  style={{
                    lineHeight: '30px',
                    paddingBottom: '6px',
                    textTransform: 'capitalize',
                    marginBottom: 0,
                  }}
                >
                  {groupName}: <span>{toUpper(alloyName)}</span>
                </XL>
              ) : (
                <XL
                  navy
                  bold
                  as="h1"
                  style={{
                    lineHeight: '30px',
                    paddingBottom: '6px',
                    textTransform: 'capitalize',
                    marginBottom: 0,
                  }}
                >
                  {groupName}
                </XL>
              )}
            </Col>
          </Row>

          {alloyDetailsResult ? (
            <DetailsWrapper
              style={
                get(alloyDetailsResult, 'has_chemical_composition')
                  ? { marginBottom: 0 }
                  : {}
              }
            >
              <Row>
                <Col>
                  <MD navy as="h3" style={{ maxWidth: '800px' }} paddingTopMd>
                    {alloyDesc}
                  </MD>
                  <HeroAttributes attributes={alloyDetailsResult} />
                </Col>
              </Row>
            </DetailsWrapper>
          ) : null}

          {alloyDetailsResult?.has_chemical_composition ? (
            <div>
              <Row>
                <Col
                  style={{ marginBottom: files && files.length ? 0 : '30px' }}
                  size="12"
                >
                  <ScrollContainer horizontal>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      <AlloyComposition
                        data={alloyDetails}
                        renderTitle={renderAlloyCompositionTitle}
                      />
                    </div>
                  </ScrollContainer>
                </Col>
              </Row>
            </div>
          ) : null}

          {files && files.length ? (
            <div style={{ marginTop: '30px', marginBottom: '30px' }}>
              <Row>
                <Col size="auto">
                  <MD navy as="h3" bold paddingBottomSm>
                    Alloy Files
                  </MD>
                  <Flex wrap="wrap">
                    {map(files, (f, i) => {
                      const title = get(f, 'original_file_name');
                      const fileId = get(f, 'id');
                      return (
                        <div
                          style={{
                            maxWidth: '350px',
                            minWidth: '250px',
                            marginBottom: '20px',
                            marginRight: '20px',
                          }}
                          key={i}
                        >
                          <DocumentDownload
                            style={{ background: '#fff' }}
                            disableDelete
                            documentItem={{ title, fileId }}
                            legacy
                          />
                        </div>
                      );
                    })}
                  </Flex>
                </Col>
              </Row>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

CategoryAlloyHero.propTypes = {
  alloyGroupDetails: PropTypes.shape({
    result: PropTypes.shape({}),
  }),
  alloyDetails: PropTypes.shape({
    result: PropTypes.shape({}),
  }),
  alloyGroupSlug: PropTypes.string,
  alloySlug: PropTypes.string,
  categories: PropTypes.shape({}),
};

const attributesDefault = {};

function HeroAttributes({ attributes = attributesDefault }) {
  return (
    <div>
      <AttributeRow>
        {attributes.corrosion_resistance_name ? (
          <AttributeWrapper size="auto">
            <Attribute>
              <SM style={{ fontSize: '13px' }} slate as="h3" bold>
                Corrosion Resistance
              </SM>
              <SM navy style={{ fontSize: '13px' }} paddingTopSm as="h3">
                {attributes.corrosion_resistance_name}
              </SM>
            </Attribute>
          </AttributeWrapper>
        ) : null}
        {attributes.metallurgy_group_name ? (
          <AttributeWrapper size="auto">
            <Attribute>
              <SM style={{ fontSize: '13px' }} slate as="h3" bold>
                Alloy Group
              </SM>
              <SM navy style={{ fontSize: '13px' }} paddingTopSm as="h3">
                {attributes.metallurgy_group_name}
              </SM>
            </Attribute>
          </AttributeWrapper>
        ) : null}

        {attributes.yield_strength_min ? (
          <AttributeWrapper size="auto">
            <Attribute>
              <SM style={{ fontSize: '13px' }} slate as="h3" bold>
                Yield Stregth
              </SM>
              <SM navy style={{ fontSize: '13px' }} paddingTopSm as="h3">
                {attributes.yield_strength_min
                  ? `${attributes.yield_strength_min || 'n/a'} - ${
                      attributes.yield_strength_max || 'n/a'
                    }`
                  : ''}
              </SM>
            </Attribute>
          </AttributeWrapper>
        ) : null}

        {attributes.wall_tollerance ? (
          <AttributeWrapper size="auto">
            <Attribute>
              <SM style={{ fontSize: '13px' }} slate as="h3" bold>
                Wall Tolerance
              </SM>
              <SM navy style={{ fontSize: '13px' }} paddingTopSm as="h3">
                {attributes.wall_tollerance}
              </SM>
            </Attribute>
          </AttributeWrapper>
        ) : null}
        {attributes.density ? (
          <AttributeWrapper size="auto">
            <Attribute>
              <SM style={{ fontSize: '13px' }} slate as="h3" bold>
                Density
              </SM>
              <SM navy style={{ fontSize: '13px' }} paddingTopSm as="h3">
                {attributes.density}
              </SM>
            </Attribute>
          </AttributeWrapper>
        ) : null}
        {attributes.pren_min ? (
          <AttributeWrapper size="auto">
            <Attribute>
              <SM style={{ fontSize: '13px' }} slate as="h3" bold>
                PREN
              </SM>
              <SM navy style={{ fontSize: '13px' }} paddingTopSm as="h3">
                {attributes.pren_min
                  ? `${attributes.pren_min || 'n/a'} - ${
                      attributes.pren_max || 'n/a'
                    }`
                  : ''}
              </SM>
            </Attribute>
          </AttributeWrapper>
        ) : null}
      </AttributeRow>
    </div>
  );
}

HeroAttributes.propTypes = {
  attributes: metallurgyShape,
};

export default CategoryAlloyHero;
